/**
 * Session trail for Stock Brief More-links. The `from` query is the
 * shareable copy; this backup survives a sub-tab click that drops search.
 *
 * Page scopes are isolated (`charting` vs `fundamentals` vs …). A hop must
 * write the ticker into the DESTINATION scope or Stock Detail / the ticker
 * box stay on whatever that page last had — or empty.
 */
import {
  parseStockReturn,
  parseViaSymbol,
  symbolFromStockPath,
} from "@/lib/stock/briefHref";

export const STOCK_RETURN_KEY = "mpp:stock-brief:return:v1";

export type StockReturnOrigin = {
  href: string;
  symbol: string;
};

export function stampStockReturn(origin: StockReturnOrigin): void {
  const href = parseStockReturn(origin.href);
  if (!href || typeof sessionStorage === "undefined") return;
  const symbol = (origin.symbol || symbolFromStockPath(href) || "")
    .trim()
    .toUpperCase()
    .replace(/\.US$/i, "");
  try {
    sessionStorage.setItem(STOCK_RETURN_KEY, JSON.stringify({ href, symbol }));
  } catch {
    /* private mode / quota */
  }
}

export function readStockReturn(): StockReturnOrigin | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STOCK_RETURN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StockReturnOrigin>;
    const href = parseStockReturn(typeof parsed.href === "string" ? parsed.href : null);
    if (!href) return null;
    const symbol =
      typeof parsed.symbol === "string"
        ? parsed.symbol.trim().toUpperCase().replace(/\.US$/i, "")
        : "";
    return { href, symbol };
  } catch {
    return null;
  }
}

export function clearStockReturn(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(STOCK_RETURN_KEY);
  } catch {
    /* ignore */
  }
}

/** Longest-prefix map from a route to its SymbolScopeProvider id. */
const PATH_SCOPES: ReadonlyArray<readonly [string, string]> = [
  ["/charts", "charting"],
  ["/fundamentals", "fundamentals"],
  ["/seasonality", "seasonality"],
  ["/options", "options"],
  ["/bets", "bets"],
  ["/scanner", "top-down"],
  ["/dashboard", "dashboard"],
  ["/vpa", "vpa"],
  ["/minervini", "minervini"],
  ["/candlesticks", "candles"],
  ["/daily-brief", "daily-brief"],
  ["/outlook", "outlook"],
];

export function scopeForPath(pathname: string): string | null {
  const path = (pathname.split(/[?#]/)[0] ?? "").replace(/\/+$/, "") || "/";
  if (path.startsWith("/stocks/")) return "global";
  const custom = path.match(/^\/p\/([^/]+)/);
  if (custom?.[1]) return `workspace:${custom[1]}`;
  let best: string | null = null;
  let bestLen = -1;
  for (const [prefix, scope] of PATH_SCOPES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      if (prefix.length > bestLen) {
        best = scope;
        bestLen = prefix.length;
      }
    }
  }
  return best;
}

export function normalizeViaSymbol(raw: string | null | undefined): string {
  return parseViaSymbol(raw) ?? "";
}

/** True when `pathname` is the workspace that stamped `originHref`.
 *  Charting's nav tab is `/charts` and the page is `/charts/chart`;
 *  Fundamentals' index is `/fundamentals` and the tab is
 *  `/fundamentals/financials`. Exact-path compare treated those as a
 *  new page, hid the return bar, and skipped the origin-scope write. */
/** Pages a Stock Brief More-link actually opens. A leftover trail must
 *  not rewrite Dashboard / Scanner / … or paint a Back bar on them. */
export function isStockHopDest(pathname: string): boolean {
  const path = (pathname.split(/[?#]/)[0] ?? "") || "/";
  return (
    path === "/fundamentals" ||
    path.startsWith("/fundamentals/") ||
    path === "/seasonality" ||
    path.startsWith("/seasonality/") ||
    path === "/options" ||
    path.startsWith("/options/") ||
    path.startsWith("/stocks/")
  );
}

export function sameReturnOrigin(originHref: string, pathname: string): boolean {
  const originPath = (originHref.split(/[?#]/)[0] ?? "").replace(/\/+$/, "") || "/";
  const path = (pathname.split(/[?#]/)[0] ?? "").replace(/\/+$/, "") || "/";
  if (originPath === path) return true;
  const originScope = scopeForPath(originPath);
  const pathScope = scopeForPath(path);
  if (!originScope || !pathScope || originScope !== pathScope) return false;
  return path.startsWith(`${originPath}/`) || originPath.startsWith(`${path}/`);
}